import { useCallback, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { restoreWeek, selectMealsForWeek } from '../redux/mealPlanSlice';
import type { PlannedMeal } from '../redux/mealPlanSlice';
import type { RootState } from '../redux/store';

export interface WeekSnapshot {
  weekStart: string;
  meals: PlannedMeal[];
}

/**
 * Snapshot-and-restore for plan mutations that are not their own inverse.
 *
 * Moving one dish can be undone by moving it back, so the planner and the
 * recipe detail screen both used to hand-roll that. Replacing a dish cannot:
 * the evicted meal is gone, and no inverse dispatch brings it back. Swapping
 * across two weeks cannot either, since both buckets change. Both screens now
 * snapshot every week the mutation touches and put those weeks back verbatim.
 *
 * Weeks are named explicitly rather than derived from "the week on screen":
 * a cross-week move rewrites a week the user is not looking at, and restoring
 * only the visible one leaves the dish duplicated across the two.
 */
export function useWeekSnapshots() {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const snapshots = useRef(new Map<string, WeekSnapshot[]>());
  const counter = useRef(0);

  /** Copy the named weeks and return the key that restores them. */
  const snapshot = useCallback((weekStarts: string[]): string => {
    const key = `undo-${++counter.current}`;
    const state = store.getState();
    const unique = [...new Set(weekStarts.filter(Boolean))];
    snapshots.current.set(key, unique.map((weekStart) => ({
      weekStart,
      meals: selectMealsForWeek(weekStart)(state).map((m) => ({ ...m })),
    })));
    return key;
  }, [store]);

  const restore = useCallback((key: string) => {
    const snaps = snapshots.current.get(key);
    if (snaps) snaps.forEach((snap) => dispatch(restoreWeek(snap)));
    snapshots.current.delete(key);
  }, [dispatch]);

  /** Drop a snapshot without restoring it — the toast expired, say. */
  const discard = useCallback((key: string) => {
    snapshots.current.delete(key);
  }, []);

  return { snapshot, restore, discard };
}
